/**
 * AI-mail — pure helpers voor de lijst (docs/ai-mail-plan.md). Geen React, zodat jest ze los test.
 */
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

export const BASIS = `${BACKEND_URL}/Api/ai-mail`;

async function vraag(pad, opties = {}) {
  const r = await authFetch(`${BASIS}${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fout = new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
    fout.status = r.status;
    throw fout;
  }
  return d;
}

export const aiMailApi = {
  status: () => vraag('/status'),
  items: (tab, id = null) => vraag(`/items?tab=${tab === 'klaar' ? 'klaar' : 'open'}${id ? `&id=${Number(id)}` : ''}`),
  stats: () => vraag('/stats'),
  goedkeuren: (id, body, opnieuw = false) => vraag(`/items/${id}/goedkeuren${opnieuw ? '?opnieuw=true' : ''}`, { method: 'POST', body }),
  afwijzen: (id) => vraag(`/items/${id}/afwijzen`, { method: 'POST', body: {} }),
  heropen: (id) => vraag(`/items/${id}/heropen`, { method: 'POST', body: {} }),
  zelfAfgehandeld: (id) => vraag(`/items/${id}/zelf-afgehandeld`, { method: 'POST', body: {} }),
  ronde: () => vraag('/ronde', { method: 'POST', body: {} }),
  melding: (aan) => vraag('/melding', { method: 'POST', body: { aan: !!aan } }),
};

/** Statuspaneel: wat de schakelaar AI_MAIL_MELDING nu doet. */
export function meldingTekst(m) {
  if (!m) return '';
  if (m.modus === 'off') return 'AI_MAIL_MELDING staat uit: er gaat geen mail.';
  if (m.modus === 'dry') return 'AI_MAIL_MELDING op proef: de mail wordt alleen gelogd.';
  if (!m.verzendklaar) return 'De notificatielaag verstuurt nu niet (dry-run of geen afzender): de mail wordt alleen gelogd.';
  return '';
}

/** ?id=…&actie=goedkeuren|afwijzen|zelf-afgehandeld uit de mail → alleen deze drie acties, verder niets. */
export function mailActie(params) {
  const id = Number(params.get('id'));
  const actie = params.get('actie');
  return {
    id: Number.isInteger(id) && id > 0 ? id : null,
    actie: ['goedkeuren', 'afwijzen', 'zelf-afgehandeld'].includes(actie) ? actie : null,
  };
}

export const MODUS_TEKST = { off: 'Uit', dry: 'Proef', on: 'Aan' };
export const MODUS_UITLEG = {
  off: 'AI-mail staat uit. Er wordt niets opgepakt.',
  dry: 'Proef: voorstellen worden gemaakt, maar goedkeuren zet nog niets in Outlook of To Do.',
  on: 'Aan: goedkeuren zet het concept in je Concepten en de taak in To Do. Er wordt nooit iets verstuurd.',
};

export const STATUS_TEKST = {
  nieuw: 'Wordt geanalyseerd', voorstel: 'Voorstel', vraag: 'Vraag', fout: 'Analyse mislukt', bezig: 'Bezig',
  uitgevoerd: 'Uitgevoerd', afgewezen: 'Afgewezen', samengevoegd: 'Samengevoegd', zelf_afgehandeld: 'Zelf afgehandeld',
};

/** Mag dit item als "zelf afgehandeld" gesloten worden? (niet tijdens uitvoeren) */
export function kanZelfAfhandelen(item) {
  return ['nieuw', 'voorstel', 'vraag', 'fout'].includes((item || {}).status);
}

/** "3 berichten in dit gesprek" (één item per Outlook-gesprek); leeg bij één bericht. */
export function gesprekTekst(item) {
  const n = Number((item || {}).berichten) || 1;
  return n > 1 ? `${n} berichten in dit gesprek` : '';
}

/** Past Toolframe bij goedkeuren de relatie op de factuur in Gripp aan? (alleen als de engine dat aanzet) */
export function grippSchrijft(item) {
  const f = ((item.voorstel || {}).extra || {}).factuur || {};
  return !!f.gripp_actie && item.gripp_schrijven === 'on' && item.gripp_status !== 'klaar';
}

/** Wat er bij goedkeuren gebeurt, in gewone taal. */
export function watGebeurt(item, { geenConcept = false, geenTaak = false, geenGripp = false } = {}) {
  const delen = [];
  if (!geenGripp && grippSchrijft(item)) delen.push('relatie op de factuur in Gripp aanpassen');
  if (!geenConcept && (item.concept_tekst || '').trim()) delen.push('concept in je Concepten');
  if (!geenTaak) delen.push('taak in je To Do');
  delen.push('categorie AI ✓');
  return delen.join(' + ');
}

/** "Aan: a, b · CC: c" (of de kandidatenkeuze bij doorsturen). */
export function ontvangersTekst(ontvangers) {
  const o = ontvangers || {};
  if (Array.isArray(o.kandidaten)) return '';
  const aan = (o.aan || []).join(', ');
  const cc = (o.cc || []).join(', ');
  if (!aan && !cc) return '';
  return [aan && `Aan: ${aan}`, cc && `CC: ${cc}`].filter(Boolean).join(' · ');
}

/** Welke velden zijn veranderd t.o.v. het voorstel → de body voor goedkeuren (strikt schema). */
export function wijzigingen(item, bewerkt) {
  const b = {};
  if (bewerkt.concept_tekst !== undefined && bewerkt.concept_tekst !== (item.concept_tekst || '')) b.concept_tekst = bewerkt.concept_tekst;
  if (bewerkt.taak_titel !== undefined && bewerkt.taak_titel !== (item.taak_titel || '')) b.taak_titel = bewerkt.taak_titel;
  if (bewerkt.geen_concept) b.geen_concept = true;
  if (bewerkt.geen_taak) b.geen_taak = true;
  if (bewerkt.geen_gripp) b.geen_gripp = true;
  if (bewerkt.origineel_id) b.origineel_id = bewerkt.origineel_id;
  // Vingerafdruk van wat je zag: is het voorstel intussen veranderd (opnieuw geanalyseerd), dan weigert de server.
  if (item.versie) b.versie = item.versie;
  return b;
}

export function euro(centen) {
  if (centen === null || centen === undefined) return '';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Number(centen) / 100);
}
